import React from "react"
import { FilterConfig } from "./store/filters-slice"
import { FilterPresentation, UseSearchReturn } from "./use-search"
import useSearchContext from "./use-search-context"
import { FacetConfig } from "./store/facets-slice"

/*
 * This hook calls register during render.
 * We don't register in an effect because we want the registration to
 * take effect during rendering- this allows hooks that consume
 * the registration to access a registered search entity while they're rendering,
 * as long as they are rendered in subsequent or child components.
 *
 * If we *did* register in an effect, hooks that consume the registration
 * would be forced to rerender needlessly. They will still rerender
 * if they subscribe to the entity and the registration is unmounted.
 *
 * We also conditionally call setState during render so that we
 * can access the entity id from previous renders of the hook. This is the
 * current approach recommended by React:
 * https://react.dev/reference/react/useState#storing-information-from-previous-renders
 *
 * Note that this causes the consuming component to render twice,
 * but the consuming components children only render once.
 */
interface EntityRegistrationMethods<Config> {
  register(id: string, config: Config): void
  deregister(id: string): void
}
export function useEntityRegistration<Config>(
  id: string,
  config: Config,
  { register, deregister }: EntityRegistrationMethods<Config>,
): void {
  const [previousId, setPreviousId] = React.useState<string | null>(null)

  if (previousId !== id) {
    if (previousId !== null) {
      deregister(previousId)
    }
    register(id, config)
    setPreviousId(id)
  }

  const onUnmount = React.useEffectEvent(() => deregister(id))
  React.useEffect(() => () => onUnmount(), [])
}

export function useFilterRegistration(
  filterId: string,
  filterConfig: FilterConfig,
  presentation?: FilterPresentation,
  searchMethods?: UseSearchReturn,
): void {
  const { filters } = useSearchContext(searchMethods)
  useEntityRegistration(filterId, filterConfig, filters)
  usePresentationRegistration(filterId, presentation, filters)
}

export function useFacetRegistration(
  facetId: string,
  facetConfig: FacetConfig,
  presentation?: FilterPresentation,
  searchMethods?: UseSearchReturn,
): void {
  const { facets, filters } = useSearchContext(searchMethods)
  useEntityRegistration(facetId, facetConfig, facets)
  useEntityRegistration(facetId, facetConfig, filters)
  usePresentationRegistration(facetId, presentation, filters)
}

function usePresentationRegistration(
  id: string,
  presentation: FilterPresentation | undefined,
  filters: Pick<
    UseSearchReturn["filters"],
    "registerPresentation" | "deregisterPresentation"
  >,
) {
  const [previousId, setPreviousId] = React.useState<string | null>(null)

  if (previousId !== id) {
    if (previousId !== null) filters.deregisterPresentation(previousId)
    if (presentation) filters.registerPresentation(id, presentation)
    setPreviousId(id)
  }

  const onUnmount = React.useEffectEvent(() =>
    filters.deregisterPresentation(id),
  )
  React.useEffect(() => () => onUnmount(), [])
}
